import type { Message, MessageCreate } from './types';
import type { AlertLevel } from 'src/interface/command-center/unsorted-types';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { isBenignAlertLevel } from 'src/interface/command-center/unsorted-type-guards';
import { renderErrorParts } from 'src/tools/http/renderErrorMessaging';
import { v4 as uuid } from 'uuid';
import { MessagesContext, type MessagesState } from './context';
import { InitialToastMessagesKey } from './toast-window';

/** @deprecated */
type LEGACY_MessageCreate = {
  type: AlertLevel;
  overridable?: boolean;
  text: ReactNode;
};

const INITIAL_TOAST_MESSAGES_KEY: InitialToastMessagesKey =
  '__INITIAL_TOAST_MESSAGES__';

const createMessage = ({
  type = 'info',
  title,
  description,
  errorMessage,
  sticky,
  overridable = sticky ? false : isBenignAlertLevel(type),
  disableCopy,
}: MessageCreate): Message => ({
  id: uuid(),
  type,
  title,
  description,
  errorMessage,
  overridable,
  sticky,
  disableCopy,
});

type Props = {
  children: ReactNode;
};

export function MessagesProvider({ children }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);

  const dismissMessage = useCallback((id: string) => {
    setMessages((currentMessages) =>
      currentMessages.filter((message) => message.id !== id),
    );
  }, []);

  const showMessage = useCallback<MessagesState['showMessage']>(
    (newMessage: MessageCreate) => {
      const message = createMessage(newMessage);

      setMessages((currentMessages) => {
        return [
          ...currentMessages.filter(({ overridable }) => !overridable),
          message,
        ];
      });
      return message.id;
    },
    [],
  );

  const showError = useCallback<MessagesState['showError']>(
    ({ error, payload, ...options }) => {
      const { brief, details } = renderErrorParts(error);
      return showMessage({
        ...options,
        type: 'error',
        title: brief,
        errorMessage: details,
      });
    },
    [showMessage],
  );

  /** @deprecated */
  const LEGACY_showMessage = useCallback((newMessage: LEGACY_MessageCreate) => {
    const message = createMessage({
      overridable: newMessage.type !== 'error',
      // need to fall back to `null` due to how RequireAtLeastOne works
      description: newMessage.text ?? null,
      type: newMessage.type,
    });
    setMessages((currentMessages) => {
      return [
        ...currentMessages.filter(({ overridable }) => !overridable),
        message,
      ];
    });
    return message.id;
  }, []);

  const providerValue: MessagesState = useMemo(
    () => ({
      messages,
      showMessage,
      showError,
      LEGACY_showMessage,
      dismissMessage,
    }),
    [messages, showMessage, showError, LEGACY_showMessage, dismissMessage],
  );

  useEffect(function showInitialMessages() {
    const initialMessages: MessageCreate[] =
      window[INITIAL_TOAST_MESSAGES_KEY] ?? [];
    if (initialMessages.length) {
      setMessages(initialMessages.map(createMessage));
    }
  }, []);

  return (
    <MessagesContext.Provider value={providerValue}>
      {children}
    </MessagesContext.Provider>
  );
}
